import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import type { BookingEventRegistration } from '@/types';

import { IconButton } from '@/components';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  BOOKING_CHANNEL_LABEL_KEYS,
  BOOKING_CLOSED_STATUSES,
  BOOKING_PAYMENT_LABEL_KEYS,
} from '@/constants';

import { BookingPersonName } from '../BookingPersonName';
import { BookingStatusBadges } from '../BookingStatusBadges';

import { isRegistrationMovable } from './utils';

type RegistrationsListProps = {
  registrations: BookingEventRegistration[];
  waveNames: Map<number, string>;
  isPending: boolean;
  onAttend: (registrationId: number) => void;
  onMarkMissed: (registrationId: number) => void;
  onMissedCommentEdit: (registrationId: number, comment: string | null) => void;
  onCancel: (registrationId: number) => void;
  onEdit: (registrationId: number) => void;
  onMove: (registrationId: number) => void;
};

export const RegistrationsList = (props: RegistrationsListProps) => {
  const {
    registrations,
    waveNames,
    isPending,
    onAttend,
    onMarkMissed,
    onMissedCommentEdit,
    onCancel,
    onEdit,
    onMove,
  } = props;
  const { t } = useTranslation();

  if (registrations.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">{t('No registrations')}</p>
    );
  }

  return (
    <div className="grid gap-3">
      {registrations.map((registration) => {
        const isClosed = BOOKING_CLOSED_STATUSES.includes(registration.status);
        const isMissable = registration.status === 'reserved';
        const isMoveAvailable = isRegistrationMovable(registration);
        const waveName =
          registration.event_wave_id === null
            ? null
            : waveNames.get(registration.event_wave_id);

        return (
          <div
            key={registration.id}
            className="grid gap-2 rounded-md border p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <BookingPersonName
                client={registration.client}
                guestInfo={registration.guest_info}
                className="text-sm font-medium"
              />

              <BookingStatusBadges
                status={registration.status}
                isUnassigned={registration.unassigned}
              />
            </div>

            <div className="text-muted-foreground grid gap-0.5 text-xs">
              {!!waveName && <span>{`${t('Wave')}: ${waveName}`}</span>}
              <span>
                {`${t('Channel')}: ${t(BOOKING_CHANNEL_LABEL_KEYS[registration.channel])}`}
              </span>
              {registration.payment_status !== 'none' && (
                <span>
                  {`${t('Payment')}: ${t(BOOKING_PAYMENT_LABEL_KEYS[registration.payment_status])}`}
                </span>
              )}
              {!!registration.missed_comment && (
                <span className="whitespace-pre-line">
                  {`${t('No-show comment')}: ${registration.missed_comment}`}
                </span>
              )}
              {registration.custom_field_answers.map((answer) => (
                <span key={answer.id}>
                  {`${answer.label}: ${answer.value || '—'}`}
                </span>
              ))}
              {!!registration.note && (
                <span className="whitespace-pre-line">
                  {`${t('Note')}: ${registration.note}`}
                </span>
              )}
              {!!registration.staff_comment && (
                <span className="wrap-break-word whitespace-pre-line">
                  {`${t('Comment')}: ${registration.staff_comment}`}
                </span>
              )}
            </div>

            <Separator />

            <div className="flex flex-wrap gap-2">
              {registration.status === 'missed' && (
                <Button
                  variant="outline"
                  onClick={() =>
                    onMissedCommentEdit(
                      registration.id,
                      registration.missed_comment,
                    )
                  }
                  disabled={isPending}
                >
                  {t('Edit no-show comment')}
                </Button>
              )}

              {!isClosed && (
                <>
                  <Button
                    onClick={() => onAttend(registration.id)}
                    disabled={isPending}
                  >
                    {t('Attendance')}
                  </Button>

                  {isMissable && (
                    <Button
                      variant="outline"
                      onClick={() => onMarkMissed(registration.id)}
                      disabled={isPending}
                    >
                      {t('Mark no-show')}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => onCancel(registration.id)}
                    disabled={isPending}
                  >
                    {t('Cancel registration')}
                  </Button>

                  {registration.event_wave_id !== null && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onMove(registration.id)}
                            disabled={isPending || !isMoveAvailable}
                          >
                            {t('Move')}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!isMoveAvailable && (
                        <TooltipContent>
                          {t('No wave to move to')}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  )}
                </>
              )}

              <IconButton
                label={t('Edit participant')}
                size="icon"
                variant="outline"
                className="ml-auto"
                onClick={() => onEdit(registration.id)}
                disabled={isPending}
              >
                <Pencil />
              </IconButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};
